'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, BookOpen, MapPin, CreditCard, Search, ChevronLeft, ChevronRight, Settings, LogOut, FileText,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  group: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard, group: 'Operations' },
  { label: 'Burial Management', href: '/burial-management', icon: BookOpen, badge: 5, group: 'Operations' },
  { label: 'Grave Plots', href: '/grave-plot-management', icon: MapPin, group: 'Operations' },
  { label: 'Deceased Records', href: '/deceased-records', icon: FileText, group: 'Records' },
  { label: 'Payments & Billing', href: '/payments-billing', icon: CreditCard, badge: 3, group: 'Finance' },
  { label: 'Public Search', href: '/public-grave-search-portal', icon: Search, group: 'Public' },
];

const groups = ['Operations', 'Records', 'Finance', 'Public'];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`sidebar-transition flex flex-col bg-card border-r border-border h-screen sticky top-0 z-30 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center border-b border-border ${collapsed ? 'justify-center p-3' : 'px-4 py-3 gap-2'}`}>
        <AppLogo size={36} />
        {!collapsed && (
          <div className="overflow-hidden">
            <span className="font-bold text-sm text-primary leading-tight block">CemeteryMS</span>
            <span className="text-xs text-muted-foreground leading-tight block">Nyandeni Municipality</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2 space-y-4">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group);
          if (items.length === 0) return null;
          return (
            <div key={`group-${group}`}>
              {!collapsed && (
                <p className="text-[11px] font-600 uppercase tracking-widest text-muted-foreground px-2 mb-1">
                  {group}
                </p>
              )}
              <div className="space-y-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  const isActive = pathname === item.href;
                  return (
                    <Link
                      key={`nav-${item.href}`}
                      href={item.href}
                      className={`nav-item relative ${isActive ? 'nav-item-active' : ''} ${
                        collapsed ? 'justify-center px-0' : ''
                      }`}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon size={18} className="shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {!collapsed && item.badge ? (
                        <span className="ml-auto text-[11px] font-700 bg-accent text-white rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                          {item.badge}
                        </span>
                      ) : null}
                      {collapsed && item.badge ? (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full" />
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-2 space-y-0.5">
        <Link href="/settings" className={`nav-item ${collapsed ? 'justify-center px-0' : ''}`} title={collapsed ? 'Settings' : undefined}>
          <Settings size={18} className="shrink-0" />
          {!collapsed && <span>Settings</span>}
        </Link>
        <button className={`nav-item w-full ${collapsed ? 'justify-center px-0' : ''}`} title={collapsed ? 'Sign Out' : undefined}>
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center shadow-card hover:bg-secondary transition-all duration-150 z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}